import { ReactElement, useRef, useState } from 'react';

import { Button } from '@components/button';
import { createForm, useCreateFormStore } from '@components/form';
import { HideContainer } from '@components/hide-container';
import { IconProps } from '@components/icons';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconAttributeTraveller } from '@components/icons/attribute-traveller';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { InfoBox } from '@components/info-box';
import { RelativeSpinner } from '@components/loading-spinner';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';
import { uuid } from '@utils/uuid';

import { useCompanyTravellerAttributeTemplates } from 'api/attributes/hooks';
import { CompanyUserOptions } from 'api/authentication/requests';
import {
  JourneyActionDetailed,
  JourneyField,
  JourneyType,
  TravellerJourneyActionDetailed,
} from 'api/types';
import { useUsers } from 'api/users/hooks';
import {
  ActionFooter,
  DescriptionFormField,
  useVariableGroups,
} from 'ui/journey-builder/node-types/common';
import { FormWithUnsavedChangesPrompt } from 'ui/journey-builder/unsaved-changes-prompt';

import {
  BaseContentProps,
  InfoBoxClosable,
  InfoBoxVariant,
  getFieldMappedAttributeFieldComparatorIds,
} from '../common';
import { FieldComponent } from '../update-fields/helpers';

type Data = {
  description: string;
  fields: JourneyField[];
};

const Form = createForm<Data>();

type AvailableActionTypes = 'update_traveller';

type UpdateTravellerFormContentProps<T extends AvailableActionTypes> = BaseContentProps<
  JourneyActionDetailed<T> | TravellerJourneyActionDetailed<T>
> & {
  journeyId?: JourneyType['shortId'];
  isTraveller?: boolean;
  filePath: string;
  onSubmit({ fields, description }: { fields: JourneyField[]; description: string }): Promise<any>;
  onCancel(): void;
};

export const UpdateFieldsFormContent = <T extends AvailableActionTypes>({
  action,
  journeyId = '',
  isTraveller = false,
  filePath,
  onCancel,
  onSubmit,
}: UpdateTravellerFormContentProps<T>) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();

  const attributeTemplatesQuery = useCompanyTravellerAttributeTemplates({
    queryData: { ordering: 'title' },
    systemAttributes: true,
  });
  const attributes = attributeTemplatesQuery.data ?? [];
  const userQuery = useUsers('?ignore_invited=true');
  const usersList = userQuery.data;

  const initialFields = useRef(action.values?.fields ?? []);
  const [fields, setFields] = useState<JourneyField[]>(
    initialFields.current.map((field) => ({ ...field, id: uuid() }))
  );
  const isDirty = formStore.useIsDirty() || initialFields.current.length !== fields.length;

  const variableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId,
    excludedTypes: ['file', 'file_multi'],
  });
  const emailVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId,
    types: ['email'],
  });
  const phoneVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId,
    types: ['phone'],
  });
  const dateVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId,
    types: ['date'],
  });
  const optionsVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId,
    types: ['select'],
  });
  const fileVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId,
    types: ['file'],
  });
  const fileMultiVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId,
    types: ['file_multi', 'file'],
  });
  const numberVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId,
    types: ['number'],
  });
  const decimalVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId,
    types: ['number', 'decimal'],
  });

  const formValues = formStore.useFormValues();

  const allFields = fields.map(
    (field) => formValues[getComparatorIds(field.id).elementId] ?? ''
  ) as string[];

  const handleSubmit = () => {
    const formFields = fields.map((field) => {
      const { elementId, lastTitleId, lastTypeId, lastOptionsId } = getComparatorIds(field.id);
      const { valueId } = getFieldMappedAttributeFieldComparatorIds(field.id);
      return {
        id: field.id,
        field: formValues[elementId],
        value: formValues[valueId],
        lastSavedTitle: formValues[lastTitleId],
        lastSavedType: formValues[lastTypeId],
        lastSavedOptions: formValues[lastOptionsId],
      } as JourneyField;
    });
    return onSubmit({ fields: formFields, description: formValues['description'] as string });
  };

  if (attributeTemplatesQuery.isLoading || userQuery.isLoading) {
    return <RelativeSpinner />;
  }

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSubmit}>
      <Stack space={6}>
        <HideContainer title={t('actionDetails')}>
          <DescriptionFormField defaultValue={action.description} isTraveller={isTraveller} />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('fields')}>
          <Stack space={4}>
            <UpdateTravellerInfoBoxClosable infoType="selectField" />
            {fields.map((field) => (
              <FieldComponent
                key={field.id}
                attributes={attributes}
                allFields={allFields}
                variableGroups={variableGroups}
                emailVariableGroups={emailVariableGroups}
                phoneVariableGroups={phoneVariableGroups}
                dateVariableGroups={dateVariableGroups}
                optionsVariableGroups={optionsVariableGroups}
                fileVariableGroups={fileVariableGroups}
                fileMultiVariableGroups={fileMultiVariableGroups}
                numberVariableGroups={numberVariableGroups}
                decimalVariableGroups={decimalVariableGroups}
                onRemove={() => setFields(fields.filter((c) => c.id !== field.id))}
                field={field}
                form={Form}
                usersList={usersList}
                filePath={filePath}
                journeyId={journeyId}
              />
            ))}
            <Stack orientation="horizontal">
              <Button
                onClick={() =>
                  setFields([
                    ...fields,
                    {
                      id: uuid(),
                      field: undefined,
                      value: undefined,
                    },
                  ])
                }
                text={t('addField')}
                iconLeft={<IconAddPlus />}
                variant="secondary"
              />
            </Stack>
            {fields.length > 0 && <UpdateTravellerInfoBox infoType="fieldsUpdated" />}
          </Stack>
        </HideContainer>
      </Stack>
      <ActionFooter
        action={action}
        isDisabled={!isDirty || fields.length === 0}
        onCancel={onCancel}
        form={Form}
      />
    </FormWithUnsavedChangesPrompt>
  );
};

const getComparatorIds = (id: string) => ({
  elementId: `${id}Element` as keyof Data,
  lastTitleId: `${id}Title` as keyof Data,
  lastTypeId: `${id}Type` as keyof Data,
  lastOptionsId: `${id}Options` as keyof Data,
});

type InfoType = 'selectField' | 'fieldsUpdated';

const TYPE_TO_VARIANT: Record<InfoType, InfoBoxVariant> = {
  selectField: 'blue',
  fieldsUpdated: 'orange',
};

const TYPE_TO_TEXT: Record<InfoType, keyof typeof locale.en> = {
  selectField: 'infoSelectField',
  fieldsUpdated: 'infoFieldsUpdated',
};

type CloseableInfoType = 'selectField';

const TYPE_TO_OPTION_KEY: Record<CloseableInfoType, keyof CompanyUserOptions> = {
  selectField: 'hasHiddenUpdateTravellerSelectField',
};

const TYPE_TO_ICON: Record<InfoType, ReactElement<IconProps>> = {
  selectField: <IconAttributeTraveller />,
  fieldsUpdated: <IconFormIndicatorWarning />,
};

export const UpdateTravellerInfoBox = ({
  infoType,
  children,
}: {
  infoType: InfoType;
  children?: ReactElement;
}) => {
  const { t } = useScopedTranslate(locale);
  const variant = TYPE_TO_VARIANT[infoType];
  const text = t(TYPE_TO_TEXT[infoType]);
  const icon = TYPE_TO_ICON[infoType];
  return <InfoBox variant={variant} text={text} icon={icon} children={children} />;
};

export const UpdateTravellerInfoBoxClosable = ({ infoType }: { infoType: CloseableInfoType }) => {
  const { t } = useScopedTranslate(locale);
  const optionKey = TYPE_TO_OPTION_KEY[infoType];
  const variant = TYPE_TO_VARIANT[infoType];
  const text = t(TYPE_TO_TEXT[infoType]);
  const icon = TYPE_TO_ICON[infoType];
  return <InfoBoxClosable optionKey={optionKey} variant={variant} text={text} icon={icon} />;
};

const locale = {
  en: {
    actionDetails: 'Action details',
    fields: 'Fields',
    addField: 'Add field',
    infoSelectField: 'Select what field you want to update',
    infoFieldsUpdated:
      'When this action is run the fields you selected will be updated with new information you provided. Be aware that this change will affect this traveller globally.',
  },
};
